/**
 * New schedule event — a direct create surface (mobile UX batch follow-up). Reached from the
 * Home "Create" section and the Jobs list "New service call" button so users no longer have to
 * discover the calendar header icon to make a job.
 *
 * Renders the shared ScheduleEventForm (optionally preset to a type via ?type=service_call),
 * creates the event via POST /schedule-events, and lands the user where the result lives:
 * a service_call also creates + links a Job -> the Jobs list; anything else -> the calendar.
 *
 * `new` is a static route so it takes precedence over schedule/[id] for /schedule/new.
 * NEVER imports @smartbox/* (mobile invariant 1). Design-system primitives only; no raw styles.
 */
import { useState } from 'react';
import { Stack, useRouter, useLocalSearchParams } from 'expo-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createScheduleEvent } from '../../../src/api/calendar';
import { Screen, Text, Card } from '../../../src/components/ui';
import {
  ScheduleEventForm,
  type ScheduleEventFormValues,
} from '../../../src/components/schedule/ScheduleEventForm';
import { extractErrorMessage } from '../../../src/lib/format';
import type { ScheduleEventDto, CreateScheduleEventRequest } from '@beam/api-types';

export default function NewScheduleEventScreen() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const params = useLocalSearchParams<{ type?: string }>();
  const [createError, setCreateError] = useState<string | null>(null);

  const isServiceCall = params.type === 'service_call';
  // Preset only the event type; the form defaults every other field. Cast is safe — the
  // form reads `initial` with optional chaining, so the absent fields fall back to defaults.
  const preset: ScheduleEventDto | undefined = isServiceCall
    ? ({ event_type: 'service_call' } as ScheduleEventDto)
    : undefined;

  const createMutation = useMutation({
    mutationFn: (body: CreateScheduleEventRequest) => createScheduleEvent(body),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['schedule-events'] });
      void queryClient.invalidateQueries({ queryKey: ['jobs-list'] });
      const dest = variables.event_type === 'service_call' ? '/(app)/jobs' : '/(app)/calendar';
      router.replace(dest as never);
    },
    onError: (err) => setCreateError(extractErrorMessage(err, 'Could not create event.')),
  });

  return (
    <>
      <Stack.Screen options={{ title: isServiceCall ? 'New service call' : 'New event' }} />
      <Screen scroll>
        {createError ? (
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {createError}
            </Text>
          </Card>
        ) : null}
        <ScheduleEventForm
          initial={preset}
          busy={createMutation.isPending}
          submitLabel="Create"
          onCancel={() => router.back()}
          onSubmit={(v: ScheduleEventFormValues) => {
            setCreateError(null);
            createMutation.mutate(v);
          }}
        />
      </Screen>
    </>
  );
}
